import { FC, useState } from 'react';
import { EditPencilIcon, ProfileIcon } from '../../../assets/icons';
import { ProfileInfoTypes } from '../../../utils/types';
import { updateUserProfileAPI } from '../../../redux/features/users/userSlice';
import { useAppDispatch } from '../../../redux/store';

type SelectedImageState = {
  profileImage: { file: File | null; preview: string };
  licenseImage: { file: File | null; preview: string };
};

const ProfileInfo: FC<{ profileInfo: ProfileInfoTypes | null }> = ({
  profileInfo: profileDetails
}) => {
  //REDUX
  const dispatch = useAppDispatch();
  // STATES
  const [loading, setLoading] = useState<boolean>(false);
  const [profileInfo, setProfileInfo] = useState<ProfileInfoTypes>({ ...profileDetails });
  const [isEdit, setIsEdit] = useState<Boolean>(false);
  const [selectedImages, setSelectedImages] = useState<SelectedImageState>({
    profileImage: { file: null, preview: '' },
    licenseImage: { file: null, preview: '' }
  });

  // ONCHANGE
  const onChange = (field: keyof ProfileInfoTypes, value: string) => {
    setProfileInfo(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  // ONCLICK T OCHOSE FILE
  const onClickToChoseFile = (type: 'profileImage' | 'licenseImage') => {
    let input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (!file) return;
      const preview = URL.createObjectURL(file);
      setSelectedImages(prev => ({
        ...prev,
        [type]: { file, preview }
      }));
    };

    input.click();
  };

  // PAYLOAD
  const payload = () => {
    const formData = new FormData();
    formData.append('fullName', profileInfo?.fullname?.trim() || '');
    formData.append('phone', profileInfo?.phone?.trim() || '');
    formData.append('address', profileInfo?.address?.trim() || '');
    formData.append('state', profileInfo?.state?.trim() || '');
    formData.append('zipcode', profileInfo?.zipCode?.trim() || '');
    formData.append('licenseNumber', profileInfo?.licenseNumber?.trim() || '');

    if (selectedImages?.profileImage?.file)
      formData.append('profileImage', selectedImages.profileImage.file);

    if (selectedImages?.licenseImage?.file)
      formData.append('licenseImage', selectedImages.licenseImage.file);

    return formData;
  };

  // ONCLICK TO SAVE
  const onClickToSave = async () => {
    setLoading(true);
    const data = await dispatch(
      updateUserProfileAPI({ formData: payload(), userId: profileInfo?.userId })
    );
    if (data.meta.requestStatus === 'fulfilled') {
      setLoading(false);
      setIsEdit(false);
      setProfileInfo({
        ...profileInfo,
        profileImage: selectedImages?.profileImage?.preview || profileInfo?.profileImage,
        licenseImage: selectedImages?.licenseImage?.preview || profileInfo?.licenseImage
      });
    }

    if (data.meta.requestStatus === 'rejected') {
      console.log('error', data);
      setLoading(false);
      setIsEdit(false);
    }
  };

  return (
    <div className='w-full border border-gray-200 rounded-lg flex flex-col min-h-40 pb-4'>
      {/* // AVATAR */}
      <div className='h-full w-full relative justify-center'>
        <div className='bg-primary w-full h-20 overflow-hidden rounded-t-lg flex justify-end px-4'>
          {/* Top-right edit / save / cancel */}
          <div className='flex items-center gap-2 z-10'>
            {!isEdit ? (
              <button
                type='button'
                className='px-3 py-1.5 text-sm border border-white text-white hover:text-black hover:bg-neutral-400  transition'
                onClick={() => setIsEdit(true)}
              >
                Edit Profile
              </button>
            ) : (
              <>
                <button
                  type='button'
                  className='px-3 py-1.5 text-sm  border border-white text-white hover:text-black hover:bg-neutral-400 transition'
                  onClick={() => setIsEdit(false)}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='px-3 py-1.5 text-sm border border-white text-black bg-neutral-400 hover:bg-neutral-500  transition'
                  onClick={() => onClickToSave()}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>
        <div className='rounded-full h-24 w-24 bg-primary absolute border-4 border-white shadow-xl top-5 flex items-center justify-center left-1/2 transform -translate-x-1/2'>
          {isEdit && selectedImages?.profileImage?.file !== null ? (
            <img
              src={selectedImages?.profileImage?.preview}
              alt='Profile Preview'
              className='object-cover w-full h-full rounded-full'
            />
          ) : profileInfo?.profileImage ? (
            <img
              src={profileInfo.profileImage}
              alt='Profile'
              className='object-cover w-full h-full rounded-full'
            />
          ) : (
            <ProfileIcon />
          )}
          {isEdit && (
            <button
              type='button'
              onClick={() => onClickToChoseFile('profileImage')}
              className='absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-md hover:bg-neutral-100 transition'
              title='Change Profile Photo'
            >
              <EditPencilIcon className='size-5' />
            </button>
          )}
        </div>
      </div>
      {/* NAME  */}
      <div className='mt-14 w-full flex flex-col gap-10 justify-center items-center'>
        {isEdit ? (
          <input
            type='text'
            className='text-2xl font-medium text-neutral-1200 text-center  border-b border-gray-300 focus:border-primary  focus:outline-none focus:ring-0 focus:ring-primary'
            value={profileInfo?.fullname}
            onChange={e => onChange('fullname', e.target.value)}
          />
        ) : (
          <h1 className='text-2xl font-medium text-neutral-1200'>{profileInfo?.fullname}</h1>
        )}
        <div className='bg-primary h-1 w-12 rounded-full'></div>
      </div>

      {/* // BELOW  */}
      <div className='flex flex-col gap-3 px-6'>
        <div className='pt-5'>
          <h2 className='text-xl font-medium text-primary pb-4'> Contact Information</h2>
          <div className='flex flex-col md:flex-row gap-3'>
            <div className='w-1/2 flex flex-col gap-1'>
              <span className='text-neutral-900 uppercase text-sm font-medium'>Email Address</span>
              <span className='font-medium text-neutral-1200'>{profileInfo?.email}</span>
            </div>
            <div className='w-1/2 flex flex-col gap-1'>
              <span className='text-neutral-900 font-medium uppercase text-sm'>Phone No</span>
              {isEdit ? (
                <input
                  type='text'
                  className='text-md text-neutral-1200 text-start border-b border-gray-300 focus:border-primary  focus:outline-none focus:ring-0 focus:ring-primary w-max'
                  value={profileInfo?.phone}
                  onChange={e => onChange('phone', e.target.value)}
                />
              ) : (
                <span className='font-medium text-neutral-1200'>{profileInfo?.phone}</span>
              )}
            </div>
          </div>
        </div>
        <div className='pt-5'>
          <h2 className='text-xl font-medium text-primary pb-4'> Address Information</h2>
          <div className='flex flex-col md:flex-row gap-3'>
            <div className='w-full md:w-1/2 flex flex-col gap-1'>
              <span className='text-neutral-900 font-medium uppercase text-sm'>Street Address</span>
              {isEdit ? (
                <input
                  type='text'
                  className='text-md text-neutral-1200 text-start border-b border-gray-300 focus:border-primary  focus:outline-none focus:ring-0 focus:ring-primary w-max'
                  value={profileInfo?.address}
                  onChange={e => onChange('address', e.target.value)}
                />
              ) : (
                <span className='font-medium text-neutral-1200'>{profileInfo?.address}</span>
              )}
            </div>
            <div className='w-full md:w-1/2 flex gap-3 flex-col xs:flex-row'>
              <div className='w-full xs:w-1/2 flex flex-col gap-1'>
                <span className='text-neutral-900 font-medium uppercase text-sm'>State</span>
                {isEdit ? (
                  <input
                    type='text'
                    className='text-md text-neutral-1200 text-start border-b border-gray-300 focus:border-primary  focus:outline-none focus:ring-0 focus:ring-primary w-max'
                    value={profileInfo?.state}
                    onChange={e => onChange('state', e.target.value)}
                  />
                ) : (
                  <span className='font-medium text-neutral-1200'>{profileInfo?.state}</span>
                )}
              </div>
              <div className='w-full xs:w-1/2 flex flex-col gap-1'>
                <span className='text-neutral-900 font-medium uppercase text-sm'>Zip Code</span>

                {isEdit ? (
                  <input
                    type='text'
                    className='text-md text-neutral-1200 text-start border-b border-gray-300 focus:border-primary  focus:outline-none focus:ring-0 focus:ring-primary w-max'
                    value={profileInfo?.zipCode}
                    onChange={e => onChange('zipCode', e.target.value)}
                  />
                ) : (
                  <span className='font-medium text-neutral-1200'>{profileInfo?.zipCode}</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className='pt-5'>
          <h2 className='text-xl font-medium text-primary pb-4'> License Information</h2>
          <div className='flex flex-wrap lg:flex-nowrap gap-3'>
            <div className='w-full md:w-1/2 flex flex-col gap-1'>
              <span className='text-neutral-900 uppercase text-sm font-medium'>License Number</span>
              {isEdit ? (
                <input
                  type='text'
                  className='text-md text-neutral-1200 text-start border-b border-gray-300 focus:border-primary  focus:outline-none focus:ring-0 focus:ring-primary w-max'
                  value={profileInfo?.licenseNumber}
                  onChange={e => onChange('licenseNumber', e.target.value)}
                />
              ) : (
                <span className='font-medium text-neutral-1200'>{profileInfo?.licenseNumber}</span>
              )}
            </div>
            <div className='w-full md:w-1/2 flex flex-col gap-1'>
              <span className='text-neutral-900 font-medium uppercase text-sm'>License Image</span>
              <div className='pt-1.5'>
                {profileInfo?.licenseImage ? (
                  <img
                    src={profileInfo.licenseImage}
                    alt='License'
                    className='aspect-[16/9] w-10/12 md:w-80 h-32 object-cover rounded-md border'
                  />
                ) : selectedImages?.licenseImage?.file !== null ? (
                  <img
                    src={selectedImages.licenseImage.preview}
                    alt='Preview'
                    className='aspect-[16/9] w-10/12 md:w-80 h-32 object-cover rounded-md border'
                  />
                ) : isEdit ? (
                  <button
                    type='button'
                    className='px-3 py-1.5 text-sm rounded-xl border border-white text-white hover:text-black bg-primary hover:bg-neutral-400 transition'
                    onClick={() => onClickToChoseFile('licenseImage')}
                  >
                    Choose Image
                  </button>
                ) : (
                  'N/A'
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileInfo;
